<template>
  <v-form ref="form" fast-fail @submit.prevent>
    <Modal
      v-if="componentProps.isOpened"
      id="create-filter-modal"
      :is-open="componentProps.isOpened"
      :title="
        componentProps.filter
          ? labels.GLOBAL_FILTER.DIALOG_TITLE_EDIT
          : labels.GLOBAL_FILTER.DIALOG_TITLE_NEW
      "
      :header-color="CONSTANTS.DIALOGS_HEADER_color"
      :card-actions-enabled="true"
      :disable-close="!componentProps.isViewMode"
      @close-dialog="cancel"
    >
      <template #body>
        <v-row class="pt-10 justify-center mb-7">
          <v-text-field
            :id="`name-field-filter-${filter?.id}`"
            ref="nameTextField"
            v-model.trim="filterName"
            :label="labels.GLOBAL_FILTER.FILTER"
            variant="outlined"
            density="compact"
            class="pt-0 filter-name-input"
            :placeholder="labels.GLOBAL_FILTER.DEFAULT_FILTER_NAME"
            :disabled="componentProps.isViewMode"
            :rules="[(name: string) => (name === '' ? 'Name must not be empty' : true)]"
          />
        </v-row>

        <v-row v-for="(_, index) in expressions" :key="index" class="justify-center pt-0">
          <!-- expressions[index] is passed to v-model as it is a reference, otherwise sending just an expression
          (which can be specified in v-for="(expression, index) in ...")
          will be passed by value and will not update the actuall expressions array -->
          <v-col>
            <global-filter-constraint
              v-model:expression="expressions[index]"
              :disabled="componentProps.isViewMode"
            />
          </v-col>
          <div class="px-3 py-3">
            <v-btn
              v-if="expressions.length > 1"
              :disabled="componentProps.isViewMode"
              variant="outlined"
              size="small"
              icon="mdi-delete-outline"
              @click="deleteExpressionsInput(index)"
            />
          </div>
        </v-row>
        <v-row class="justify-center mb-7">
          <v-btn
            :disabled="componentProps.isViewMode"
            variant="outlined"
            icon="mdi-plus"
            size="small"
            @click="addExpressionsInput"
          />
        </v-row>
      </template>

      <template #btn-group>
        <v-row v-if="!componentProps.isViewMode" class="justify-center my-5">
          <v-col cols="auto">
            <v-btn-group class="btn-group">
              <v-btn class="secondary-btn" variant="flat" @click="cancel">
                {{ labels.GLOBAL_FILTER.CANCEL }}
              </v-btn>
            </v-btn-group>
          </v-col>

          <v-col cols="auto">
            <v-menu
              v-model="isMenuOpen"
              location="bottom"
              transition="fade-transition"
              :target="actionGroup"
            >
              <template #activator="{ props }">
                <v-btn-group ref="actionGroup" divided text class="btn-group">
                  <v-btn type="submit" variant="flat" class="primary-btn" @click="saveFilter">
                    {{ labels.GLOBAL_FILTER.SAVE }}
                  </v-btn>
                  <v-btn icon="mdi-menu-down" v-bind="props" class="toggle-btn">
                    <v-icon :class="isMenuOpen ? 'chevron-up' : 'chevron-down'"
                      >mdi-chevron-down</v-icon
                    >
                  </v-btn>
                </v-btn-group>
              </template>
              <div class="mt-1">
                <v-btn type="submit" variant="flat" class="primary-btn" @click="saveAndApplyFilter">
                  {{ labels.GLOBAL_FILTER.SAVE_AND_APPLY }}
                </v-btn>
              </div>
            </v-menu>
          </v-col>
        </v-row>

        <v-row v-else class="justify-center my-5">
          <v-btn class="primary-btn" variant="flat" @click="editFilter">
            {{ labels.GLOBAL_FILTER.EDIT }}
          </v-btn>
          <v-btn class="primary-btn ml-5" variant="flat" @click="applyFilter">
            {{ labels.GLOBAL_FILTER.APPLY }}
          </v-btn>
        </v-row>
      </template>
    </Modal>
  </v-form>
</template>

<script setup lang="ts">
  import CONSTANTS from '@/constants/Config.constants'
  import labels from '@/translations/labels.json'
  import Modal from '@/components/Modal/Modal.vue'
  import { ref, watch } from 'vue'
  import { useGlobalFilterStore } from '@/stores/globalFilterStore'
  import { Expression, GlobalFilter, SingleExpression } from '@/models'
  import { useProjectStore } from '@/stores/projectStore'
  import GlobalFilterConstraint from '@/components/Inputs/GlobalFilterConstraint.vue'
  import { storeToRefs } from 'pinia'
  import { buildExpression, flattenExpression } from '@/utils/global-filters'
  import { VForm } from 'vuetify/lib/components/index.mjs'
  import { Ref } from 'vue'

  interface Props {
    isOpened: boolean
    filter: GlobalFilter | undefined
    isViewMode: boolean
  }

  const componentProps = defineProps<Props>()

  const emit = defineEmits<{
    (event: 'closeDialog'): void
    (event: 'disableViewMode', value: boolean): void
  }>()

  const globalFiltersStore = useGlobalFilterStore()
  const projectStore = useProjectStore()

  const { currentProject } = storeToRefs(projectStore)

  const filterName = ref<string>(componentProps.filter?.name ?? '')

  const expressions = ref<Array<SingleExpression | undefined>>([])

  const form = ref<VForm | null>(null)
  const actionGroup = ref<Element | null>(null) as Ref<Element>
  const isMenuOpen = ref(false)

  watch(
    () => componentProps.filter?.expression,
    (value) => (expressions.value = flattenExpression(value)),
  )

  watch(
    () => componentProps.isOpened,
    () => {
      if (componentProps.isOpened) {
        filterName.value = componentProps.filter?.name ?? ''
      }
    },
  )

  async function saveFilter(): Promise<GlobalFilter | undefined> {
    if (!currentProject?.value || !form.value) return

    const result = await form.value.validate()
    if (!result.valid) return

    const expression: Expression | null = buildExpression(
      expressions.value.filter((e): e is SingleExpression => e !== undefined),
    )

    let filter
    if (componentProps.filter) {
      const globalFilterToUpdate: GlobalFilter = {
        name: filterName.value,
        id: componentProps.filter.id,
        expression,
      }

      filter = await globalFiltersStore.updateGlobalFilter(
        currentProject.value.id,
        globalFilterToUpdate,
      )
    } else {
      const newGlobalFilter: Omit<GlobalFilter, 'id'> = {
        name: filterName.value,
        expression,
      }

      filter = await globalFiltersStore.createNewGlobalFilter(
        currentProject.value.id,
        newGlobalFilter,
      )
    }
    emit('closeDialog')
    return filter
  }

  function editFilter(): void {
    emit('disableViewMode', false)
  }

  function applyFilter(): void {
    globalFiltersStore.applyGlobalFilter(componentProps.filter)
  }

  async function saveAndApplyFilter() {
    const filter = await saveFilter()
    globalFiltersStore.applyGlobalFilter(filter)
  }
  function cancel(): void {
    // When closing the dialog for a new filter, remove any left-over constraints
    if (!componentProps.filter) {
      expressions.value = []
    }

    emit('closeDialog')
  }

  async function addExpressionsInput(): Promise<void> {
    if (form.value && expressions.value.length > 0) {
      const result = await form.value.validate()

      if (!result.valid) return
    }

    expressions.value.push(undefined)
  }

  function deleteExpressionsInput(index: number) {
    if (expressions.value.length > 1) {
      expressions.value.splice(index, 1)
    }
  }
</script>

<style scoped>
  .inline-header-container {
    display: inline-flex;
    align-items: center;
  }

  .edit-icon {
    margin-left: 10px; /* Add space between the text and the icon */
  }

  .filter-name-input {
    max-width: 400px;
  }

  .primary-btn {
    background-color: rgb(var(--v-theme-cyan-dark));
    color: rgb(var(--v-theme-surface));
  }

  .secondary-btn {
    background-color: rgb(var(--v-theme-itemis-hidden-grey));
    color: rgb(var(--v-theme-surface));
  }

  .btn-group {
    height: 2.5rem;
  }
  .toggle-btn {
    background-color: rgb(var(--v-theme-cyan-dark));
    color: rgb(var(--v-theme-surface));
    margin: 0 !important;
    padding: 1px !important;
    width: auto;
  }

  .chevron-down {
    transform: rotate(0deg);
    transition: transform 0.2s ease-in-out;
  }
  .chevron-up {
    transform: rotate(180deg);
    transition: transform 0.2s ease-in-out;
  }
</style>

<!-- Not scoped because #create-filter-modal is created outside of this component! -->
<style>
  #create-filter-modal .v-card-title {
    min-width: 1200px;
    justify-content: center;
    display: flex;
  }
</style>
∏
